import { DateTime } from 'luxon';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { FieldPath, UseFormReturn } from 'react-hook-form';

import { RESERVATION_VALIDATE_DEBOUNCE_MS } from '@/data/constants';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectCurrentPassenger } from '@/data/passenger.slice';
import reservationUtils from '@/services/reservationUtils';

import {
  getAggregatedFormValues,
  type ReservationFormMainEvent,
} from './machines/reservationFormMain.machine';
import type {
  ReservationFormInput,
  ReservationFormValues,
} from './schema/reservationForm.schema';
import { useReservationFormMachines } from './ReservationFormMachinesContext';
import { shouldValidateReservation } from './utils/shouldValidateReservation';

function omitEmptyStringValues<T>(value: T): T {
  if (value === null || value === undefined) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => omitEmptyStringValues(item)) as T;
  }
  if (typeof value === 'object') {
    if (value instanceof DateTime) {
      return value;
    }
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      if (val === '') {
        continue;
      }
      result[key] =
        val !== null &&
        val !== undefined &&
        typeof val === 'object' &&
        !(val instanceof DateTime)
          ? omitEmptyStringValues(val)
          : val;
    }
    return result as T;
  }
  return value;
}

export function useReservationFieldEditValidation(
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>,
) {
  const { mainActor } = useReservationFormMachines();
  const currentPassenger = useAppSelector(selectCurrentPassenger);
  const lastEditedFieldRef = useRef<FieldPath<ReservationFormInput> | undefined>(
    undefined,
  );

  const sendFormFieldEdited = useCallback(() => {
    const snap = mainActor.getSnapshot();
    if (snap.matches('submitting')) {
      return;
    }
    const fieldEditedEvent: ReservationFormMainEvent = {
      type: 'FIELD_EDITED',
      editedField: lastEditedFieldRef.current,
    };
    if (!shouldValidateReservation(snap.context, fieldEditedEvent)) {
      return;
    }
    const clientBC = currentPassenger?.clientBC ?? '';
    const passengerUUID = currentPassenger?.passengerUUID ?? '';
    if (!clientBC || !passengerUUID) {
      return;
    }
    const aggregated = getAggregatedFormValues(snap.context);
    const multiLegTrips = form.getValues('trips');
    const mergedValues = omitEmptyStringValues({
      ...aggregated,
      trips: aggregated.trips.map((mt, i) => ({
        ...mt,
        ...(multiLegTrips?.[i] ?? {}),
      })),
    }) as ReservationFormValues;

    const reservation =
      reservationUtils.getPartialReservationFromReservationForm(
        clientBC,
        passengerUUID,
        mergedValues,
      );
    mainActor.send({
      type: 'FIELD_EDITED',
      reservation,
      editedField: lastEditedFieldRef.current,
    });
  }, [mainActor, currentPassenger, form]);

  const sendFormFieldEditedRef = useRef(sendFormFieldEdited);
  sendFormFieldEditedRef.current = sendFormFieldEdited;

  const debouncedSendFormFieldEdited = useMemo(
    () =>
      debounce(() => {
        sendFormFieldEditedRef.current();
      }, RESERVATION_VALIDATE_DEBOUNCE_MS),
    [],
  );

  useEffect(() => {
    const subscription = form.watch((_value, info) => {
      if (info?.name) {
        lastEditedFieldRef.current = info.name;
      }
      debouncedSendFormFieldEdited();
    });
    return () => {
      subscription.unsubscribe();
      debouncedSendFormFieldEdited.cancel();
    };
  }, [form, debouncedSendFormFieldEdited]);
}
